// ============================================================
//  ShopPage.js — the public store page  /s/:slug
//  Browse products with photos, build a cart, then "Send order on WhatsApp":
//  we save the cart and open WhatsApp with "order <code>"; the bot picks it up
//  and handles delivery + payment in the chat.
// ============================================================
import './ShopPage.css';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicStore } from '../../api/client';

// public store API (no login) — kept here so this page doesn't depend on client.js changes
const PUBLIC_API = process.env.REACT_APP_API_URL || '/api';
const post = body => fetch(`${PUBLIC_API}/store/public.php`, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
}).then(r => r.json());
const sendStoreCart = (slug, items, discount = '') => post({ slug, items, discount });
const checkStoreCode = (slug, code, subtotal) => post({ slug, check: code, subtotal });

const naira = n => `₦${Number(n || 0).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`;
const KEY = slug => `ck_cart_${slug}`;
const readCart = slug => { try { return JSON.parse(localStorage.getItem(KEY(slug))) || {}; } catch { return {}; } };
// cart keys: "<productId>" or "<productId>~<optionId>"
const unit = (p, v) => (v ? v.price : (p.salePrice ?? p.price));

function Price({ p }) {
  const opts = p.variants || [];
  const prices = opts.length ? opts.map(v => v.price) : [unit(p)];
  const min = Math.min(...prices);
  return (
    <span className="shp-price">
      {p.salePrice != null && min === p.salePrice && <s>{naira(p.price)}</s>}
      {Math.max(...prices) !== min && <small>from </small>}{naira(min)}
    </span>
  );
}

export default function ShopPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [cart, setCart] = useState(() => readCart(slug));
  const [cat, setCat] = useState('');
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(null);       // product shown big
  const [showCart, setShowCart] = useState(false);

  useEffect(() => {
    getPublicStore(slug).then(d => {
      if (!d.success) { setErr(d.message || 'Store not found'); return; }
      setData(d);
      document.title = d.store.name;
    }).catch(() => setErr('Couldn’t load the store. Check your connection.'));
  }, [slug]);
  useEffect(() => { try { localStorage.setItem(KEY(slug), JSON.stringify(cart)); } catch {} }, [cart, slug]);

  const tiles = useMemo(() => {
    if (!data) return [];
    const have = new Set(data.products.map(p => p.category));
    const listed = (data.categories || []).filter(c => have.has(c.name));
    const extra = [...have].filter(n => !listed.some(c => c.name === n)).map(n => ({ name: n, emoji: '🛍️' }));
    return [...listed, ...extra];
  }, [data]);
  const cats = useMemo(() => tiles.map(c => c.name), [tiles]);
  useEffect(() => { if (cats.length && !cats.includes(cat)) setCat(cats[0]); }, [cats]); // eslint-disable-line react-hooks/exhaustive-deps
  const byId = useMemo(() => Object.fromEntries((data?.products || []).map(p => [p.id, p])), [data]);
  const lines = Object.entries(cart).map(([key, qty]) => {
    const [pid, vid] = key.split('~');
    const p = byId[pid];
    if (!p || qty < 1) return null;
    const v = vid ? (p.variants || []).find(x => x.id === vid) : null;
    if ((p.variants || []).length ? (!v || v.soldOut) : (vid || p.soldOut)) return null;
    return { key, p, v, qty, price: unit(p, v), name: p.name + (v ? ` · ${v.name}` : '') };
  }).filter(Boolean);
  const count = lines.reduce((a, l) => a + l.qty, 0);
  const subtotal = lines.reduce((a, l) => a + l.qty * l.price, 0);
  const inCart = pid => lines.filter(l => l.p.id === pid).reduce((a, l) => a + l.qty, 0);
  const change = (key, d) => setCart(c => { const n = Math.max(0, Math.min(50, (c[key] || 0) + d)); const x = { ...c, [key]: n }; if (!n) delete x[key]; return x; });

  // discount code: re-checked whenever the cart total changes
  const [code, setCode] = useState('');
  const [deal, setDeal] = useState(null);       // { ok, amount, message, code }
  useEffect(() => {
    if (!deal?.code || !subtotal) return;
    let live = true;
    checkStoreCode(slug, deal.code, subtotal).then(r => { if (live && r.success) setDeal(r); }).catch(() => {});
    return () => { live = false; };
  }, [subtotal]); // eslint-disable-line react-hooks/exhaustive-deps
  async function applyCode(e) {
    e.preventDefault();
    if (!code.trim()) return;
    try { const r = await checkStoreCode(slug, code.trim(), subtotal); setDeal(r.success ? r : { ok: false, message: r.message || 'Couldn’t check that code' }); }
    catch { setDeal({ ok: false, message: 'Couldn’t check that code. Try again.' }); }
  }
  const off = deal?.ok ? deal.amount : 0;
  const total = Math.max(0, subtotal - off);

  async function send() {
    setSending(true);
    try {
      const r = await sendStoreCart(slug, lines.map(l => ({ id: l.key, qty: l.qty })), deal?.ok ? deal.code : '');
      if (!r.success) { setErr(r.message); setSending(false); return; }
      try { localStorage.removeItem(KEY(slug)); } catch {}
      window.location.href = r.waLink;
    } catch { setErr('Couldn’t send your order. Try again.'); setSending(false); }
  }

  if (err && !data) return <main className="shp shp-center"><p>{err}</p></main>;
  if (!data) return <main className="shp shp-center"><span className="shp-spin" aria-label="Loading" /></main>;
  const s = data.store;
  const list = data.products.filter(p => p.category === cat);

  return (
    <main className="shp">
      <div className="shp-cover">{s.cover && <img src={s.cover} alt="" />}</div>
      <header className="shp-head">
        <span className="shp-logo">{s.logo ? <img src={s.logo} alt="" /> : s.name.slice(0, 2).toUpperCase()}</span>
        <h1>{s.name}</h1>
        {s.about && <p>{s.about}</p>}
        <p className={`shp-open ${s.isOpen ? '' : 'is-closed'}`}>{s.isOpen ? 'Open now' : (s.closedMsg || 'Closed right now')}{s.freeOver ? ` · free delivery over ${naira(s.freeOver)}` : ''}</p>
      </header>

      {cats.length > 1 && (
        <nav className="shp-tiles" aria-label="Categories">
          {tiles.map(c => (
            <button key={c.name} type="button" aria-pressed={cat === c.name} className={cat === c.name ? 'is-on' : ''} onClick={() => setCat(c.name)}>
              <span className="shp-tile-img" aria-hidden="true">{c.emoji || '🛍️'}</span>
              <span className="shp-tile-name">{c.name}</span>
            </button>
          ))}
        </nav>
      )}
      <h2 className="shp-h2">{cat}<small>{list.length} item{list.length === 1 ? '' : 's'}</small></h2>

      <ul className="shp-grid">
        {list.map(p => (
          <li key={p.id} className={p.soldOut ? 'is-out' : ''}>
            <button type="button" className="shp-img" onClick={() => setOpen(p)} aria-label={`See photos of ${p.name}`}>
              {p.photos[0] ? <img src={p.photos[0]} alt="" loading="lazy" /> : <span>No photo</span>}
              {p.photos.length > 1 && <em>{p.photos.length} photos</em>}
              {p.soldOut ? <i className="shp-badge is-out">Sold out</i> : p.fewLeft ? <i className="shp-badge">Only {p.fewLeft} left</i> : null}
            </button>
            <div className="shp-info">
              <b>{p.name}</b>
              <Price p={p} />
            </div>
            {p.soldOut ? null : (p.variants || []).length ? (
              <button type="button" className={`shp-add ${inCart(p.id) ? 'is-in' : ''}`} onClick={() => setOpen(p)} disabled={!s.isOpen}>
                {inCart(p.id) ? `${inCart(p.id)} in cart · add` : `Choose ${(p.variantLabel || 'option').toLowerCase()}`}</button>
            ) : cart[p.id] ? (
              <div className="shp-qty">
                <button type="button" aria-label={`Remove one ${p.name}`} onClick={() => change(p.id, -1)}>−</button>
                <b aria-live="polite">{cart[p.id]}</b>
                <button type="button" aria-label={`Add one ${p.name}`} onClick={() => change(p.id, 1)}>+</button>
              </div>
            ) : <button type="button" className="shp-add" onClick={() => change(p.id, 1)} disabled={!s.isOpen}>Add to cart</button>}
          </li>
        ))}
      </ul>

      {open && <Gallery p={open} onClose={() => setOpen(null)} onAdd={key => { change(key, 1); setOpen(null); }} canAdd={s.isOpen && !open.soldOut} />}
      {showCart && <CartSheet lines={lines} change={change} subtotal={subtotal} total={total} off={off} deal={deal} hasCodes={data.hasCodes}
        code={code} setCode={setCode} applyCode={applyCode} clearCode={() => { setDeal(null); setCode(''); }}
        onClose={() => setShowCart(false)} onSend={send} sending={sending} canSend={!!count && s.isOpen && s.canOrder} />}

      <footer className="shp-bar">
        {err && <p className="shp-err">{err}</p>}
        <button type="button" className="shp-send" disabled={!count || sending || !s.isOpen || !s.canOrder} onClick={() => setShowCart(true)}>
          <span className="shp-count">{count}</span>
          <span>{count ? 'View cart & order' : 'Add items to order'}</span>
          <span>{off > 0 && <s>{naira(subtotal)}</s>}{naira(total)}</span>
        </button>
        <small>You’ll choose delivery and pay in the chat · Store by CommandKit</small>
      </footer>
    </main>
  );
}

function Gallery({ p, onClose, onAdd, canAdd }) {
  const [i, setI] = useState(0);
  const opts = p.variants || [];
  const [pick, setPick] = useState(opts.length === 1 && !opts[0].soldOut ? opts[0].id : '');
  useEffect(() => { const k = e => { if (e.key === 'Escape') onClose(); }; document.addEventListener('keydown', k); return () => document.removeEventListener('keydown', k); }, [onClose]);
  const n = p.photos.length;
  const v = opts.find(o => o.id === pick);
  return (
    <div className="shp-gal" role="dialog" aria-modal="true" aria-label={p.name} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="shp-gal-box">
        <div className="shp-gal-img">
          {n ? <img src={p.photos[i]} alt={`${p.name} photo ${i + 1} of ${n}`} /> : <span>No photo</span>}
          {n > 1 && <>
            <button type="button" className="shp-nav is-l" aria-label="Previous photo" onClick={() => setI((i - 1 + n) % n)}>‹</button>
            <button type="button" className="shp-nav is-r" aria-label="Next photo" onClick={() => setI((i + 1) % n)}>›</button>
            <span className="shp-dots">{p.photos.map((_, k) => <i key={k} className={k === i ? 'is-on' : ''} />)}</span>
          </>}
        </div>
        <div className="shp-gal-body">
          <b>{p.name}</b>
          {v ? <span className="shp-price">{p.salePrice != null && v.price === p.salePrice && <s>{naira(p.price)}</s>}{naira(v.price)}</span> : <Price p={p} />}
          {p.description && <p>{p.description}</p>}
          {opts.length > 0 && (
            <div className="shp-opts" role="radiogroup" aria-label={p.variantLabel || 'Option'}>
              <span className="shp-opts-k">{p.variantLabel || 'Option'}{v ? `: ${v.name}` : ''}</span>
              <div>
                {opts.map(o => (
                  <button key={o.id} type="button" role="radio" aria-checked={pick === o.id} disabled={o.soldOut}
                    className={pick === o.id ? 'is-on' : ''} onClick={() => setPick(o.id)}>
                    {o.name}{o.soldOut ? <small>sold out</small> : o.fewLeft ? <small>{o.fewLeft} left</small> : null}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="shp-gal-actions">
            <button type="button" className="shp-ghost" onClick={onClose}>Close</button>
            {canAdd && <button type="button" className="shp-add is-big" disabled={opts.length > 0 && !v} onClick={() => onAdd(v ? `${p.id}~${v.id}` : p.id)}>
              {opts.length > 0 && !v ? `Choose a ${(p.variantLabel || 'option').toLowerCase()}` : 'Add to cart'}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function CartSheet({ lines, change, subtotal, total, off, deal, hasCodes, code, setCode, applyCode, clearCode, onClose, onSend, sending, canSend }) {
  useEffect(() => { const k = e => { if (e.key === 'Escape') onClose(); }; document.addEventListener('keydown', k); return () => document.removeEventListener('keydown', k); }, [onClose]);
  useEffect(() => { if (!lines.length) onClose(); }, [lines.length, onClose]);
  return (
    <div className="shp-gal" role="dialog" aria-modal="true" aria-label="Your cart" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="shp-gal-box shp-cart">
        <div className="shp-cart-head"><b>Your cart</b><button type="button" className="shp-x" aria-label="Close cart" onClick={onClose}>×</button></div>
        <ul className="shp-lines">
          {lines.map(l => (
            <li key={l.key}>
              <span className="shp-line-img">{l.p.photos[0] ? <img src={l.p.photos[0]} alt="" /> : null}</span>
              <span className="shp-line-info"><b>{l.p.name}</b>{l.v && <small>{l.p.variantLabel || 'Option'}: {l.v.name}</small>}<span>{naira(l.price * l.qty)}</span></span>
              <span className="shp-qty">
                <button type="button" aria-label={`Remove one ${l.name}`} onClick={() => change(l.key, -1)}>−</button>
                <b aria-live="polite">{l.qty}</b>
                <button type="button" aria-label={`Add one ${l.name}`} onClick={() => change(l.key, 1)}>+</button>
              </span>
            </li>
          ))}
        </ul>
        {hasCodes && (deal?.ok ? (
          <div className="shp-deal is-ok"><span>🏷️ <b>{deal.code}</b> — {deal.message}</span><button type="button" onClick={clearCode}>Remove</button></div>
        ) : (
          <form className="shp-code" onSubmit={applyCode}>
            <label className="shp-sr" htmlFor="shp-code">Discount code</label>
            <input id="shp-code" value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="Discount code" maxLength={20} autoComplete="off" />
            <button type="submit" disabled={!code.trim()}>Apply</button>
            {deal && !deal.ok && <p role="alert">{deal.message}</p>}
          </form>
        ))}
        <dl className="shp-sum">
          <div><dt>Subtotal</dt><dd>{naira(subtotal)}</dd></div>
          {off > 0 && <div className="is-off"><dt>Discount</dt><dd>−{naira(off)}</dd></div>}
          <div className="is-total"><dt>Total</dt><dd>{naira(total)}</dd></div>
        </dl>
        <button type="button" className="shp-send" disabled={!canSend || sending} onClick={onSend}>
          <i className="shp-wa" aria-hidden="true">✓</i><span>{sending ? 'Opening WhatsApp…' : 'Send order on WhatsApp'}</span><span>{naira(total)}</span>
        </button>
        <small className="shp-note">Delivery is added in the chat, where you also pay.</small>
      </div>
    </div>
  );
}
